// src/admin/screens/admins/modals/ToggleReadonlyModal.tsx
// Flips admin_users.is_readonly. A read-only admin keeps their base role
// but RLS WITH CHECK clauses block every write (migration 14). Useful
// for auditors / observers who need to read everything but never mutate.
import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import type { AdminRow } from '@admin/hooks/useAdmins';
import styles from './AdminModals.module.css';

interface Props {
  admin: AdminRow;
  onClose: (refresh: boolean) => void;
}

export default function ToggleReadonlyModal({ admin, onClose }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const nextValue = !admin.is_readonly;
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (reason.trim().length < 10) {
      toast({ kind: 'error', msg: 'Reason must be at least 10 characters.' });
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase
        .from('admin_users')
        .update({ is_readonly: nextValue })
        .eq('id', admin.id);
      if (error) throw error;
      await recordAudit({
        action: 'admin.set_readonly',
        target_type: 'admin',
        target_id: admin.id,
        payload: {
          from: admin.is_readonly,
          to: nextValue,
          reason: reason.trim(),
        },
      });
      toast({
        kind: 'success',
        msg: nextValue
          ? 'Admin is now read-only. Writes blocked on next JWT refresh.'
          : 'Read-only override removed.',
      });
      await qc.invalidateQueries({ queryKey: ['admin', admin.id] });
      await qc.invalidateQueries({ queryKey: ['admins'] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose(false)}
      title={
        nextValue
          ? `Set read-only — ${admin.display_name}`
          : `Remove read-only — ${admin.display_name}`
      }
    >
      <form onSubmit={submit} className={styles.form}>
        <p className={styles.lead}>
          {nextValue
            ? `Base role (${admin.role}) is unchanged. All writes will be rejected by RLS (is_readonly check) as soon as the JWT refreshes.`
            : 'Admin regains their base-role write capabilities immediately.'}
        </p>
        <FormField
          label="Reason"
          htmlFor="ro-reason"
          hint="Required and audit-logged."
          required
        >
          <textarea
            id="ro-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>
        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onClose(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            {nextValue ? 'Set read-only' : 'Remove read-only'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
